import React, { useMemo } from 'react';
import { Eye, ChevronLeft, Database, MessageSquare, CheckCircle2, XCircle, Image, BookOpen, ShieldCheck, Tag } from 'lucide-react';
import { Badge } from '../../../../components/ui/Badge';
import { type Question, getQuestionWithExplanation } from '../../../../content';
import { QuestionCardClean } from '../../../../practice/components/QuestionCardClean';
import { QuestionRendererFactory } from '../../../../practice/components/renderers/QuestionRendererFactory';
import { VisualAidFactory } from '../../../../visuals/components/VisualAidFactory';
interface QuestionPreviewPanelProps {
  questionId: string | null;
  onBack?: () => void;
  accentColor?: 'blue' | 'purple' | 'emerald' | 'amber';
  showBackOnMobile?: boolean;
  onNavigateToGovernance?: (questionId: string) => void;
}
const ACCENT_COLORS = {
  blue: {
    bg: 'bg-blue-50',
    border: 'border-l-blue-500',
    text: 'text-blue-600'
  },
  purple: {
    bg: 'bg-purple-50',
    border: 'border-l-purple-500',
    text: 'text-purple-600'
  },
  emerald: {
    bg: 'bg-emerald-50',
    border: 'border-l-emerald-500',
    text: 'text-emerald-600'
  },
  amber: {
    bg: 'bg-amber-50',
    border: 'border-l-amber-500',
    text: 'text-amber-600'
  }
};
export function QuestionPreviewPanel({
  questionId,
  onBack,
  accentColor = 'blue',
  showBackOnMobile = true,
  onNavigateToGovernance
}: QuestionPreviewPanelProps) {
  const question = useMemo(() => {
    if (!questionId) return null;
    return getQuestionWithExplanation(questionId);
  }, [questionId]);
  const colors = ACCENT_COLORS[accentColor];
  const visualCount = useMemo(() => {
    if (!question) return 0;
    return (question.stimulusAids?.length || 0) + (question.supportAids?.length || 0);
  }, [question]);
  // Tier color helper
  const getTierColor = (tier?: number): 'neutral' | 'success' | 'info' | 'warning' => {
    if (!tier) return 'neutral';
    if (tier <= 3) return 'success';
    if (tier <= 6) return 'info';
    return 'warning';
  };
  // Empty state
  if (!question) {
    return <div className="flex-1 flex items-center justify-center text-gray-400 p-6 bg-slate-50" data-id="element-6068" data-mp="y58n23fthyj2">
        <div className="text-center" data-id="element-6069" data-mp="12w72z1ww8uls">
          <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-4" data-id="element-6070" data-mp="12x8hxhh1618s">
            <Eye className="w-8 h-8 text-gray-300" data-id="element-6071" data-mp="17tyys70nt0v9" />
          </div>
          <p className="font-semibold text-gray-500" data-id="element-6072" data-mp="b5j3duavbxa8">Selecteer een vraag</p>
          <p className="text-sm mt-1 text-gray-400" data-id="element-6073" data-mp="f6mlknge25em">
            Kies een vraag uit de lijst om de preview te bekijken
          </p>
        </div>
      </div>;
  }
  return <div className="flex-1 min-h-0 overflow-y-auto bg-slate-50" style={{
    WebkitOverflowScrolling: 'touch'
  }} data-id="element-6074" data-mp="k7sfgolsbypq">
      <div className="p-4 sm:p-6 md:p-8 pb-8 sm:pb-12" data-id="element-6075" data-mp="1vle488nqfyaq">
        <div className="max-w-3xl mx-auto" data-id="element-6076" data-mp="1vmyydx64xf82">
          {/* Mobile back button */}
          {showBackOnMobile && onBack && <div className="mb-4 lg:hidden" data-id="element-6077" data-mp="3l1qftcsjn00k">
              <button onClick={onBack} className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-gray-600 bg-white rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors" data-id="element-6078" data-mp="3l0ou8edwiv4l">
                <ChevronLeft className="w-4 h-4" data-id="element-6079" data-mp="36ddf62iigszq" />
                Terug naar lijst
              </button>
            </div>}

          {/* Question header badges */}
          <div className="mb-4 flex flex-wrap items-center gap-2" data-id="element-6080" data-mp="3295qg5c1zd1i">
            <Badge variant="info" size="sm" data-id="element-6081" data-mp="2xaug7thuj0ir">
              {question.questionType}
            </Badge>
            <span className="text-xs text-gray-500 font-mono truncate max-w-[200px]" data-id="element-6082" data-mp="2sdujwaastc4u">
              {question.familyId}
            </span>
            {visualCount > 0 && <div className="flex items-center gap-1.5 text-xs font-medium text-purple-600 bg-purple-50 px-2 py-1 rounded-md border border-purple-100" data-id="element-6083" data-mp="qkwn63dwhzem">
                <Image className="w-3.5 h-3.5" data-id="element-6084" data-mp="ll1z07lifnx9" />
                {visualCount} visual{visualCount > 1 ? 's' : ''}
              </div>}
          </div>

          {/* Question Card */}
          <QuestionCardClean question={question} data-id="element-6085" data-mp="ykb36c0v3hiv">
            <QuestionRendererFactory question={question} onAnswer={() => {}} data-id="element-6086" data-mp="jod009sitcx5" />
          </QuestionCardClean>

          {onNavigateToGovernance && <div className="mt-4 flex justify-end" data-id="element-6087" data-mp="oqarfrb2re16">
              <button type="button" onClick={() => onNavigateToGovernance(question.id)} className="inline-flex items-center gap-2 rounded-lg border border-indigo-200 bg-white px-3 py-2 text-sm font-semibold text-indigo-700 shadow-sm transition-colors hover:bg-indigo-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500" data-id="element-6088" data-mp="aw1ixejar1tu">
                <ShieldCheck className="w-4 h-4" data-id="element-6089" data-mp="3o44prainnfdp" />
                Bekijk validatie
              </button>
            </div>}

          {/* Metadata & Feedback Section */}
          <div className="mt-6 bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden" data-id="element-6090" data-mp="39hxhts04es8w">
            {/* Metadata Grid */}
            <div className="p-4 sm:p-5 border-b border-gray-100" data-id="element-6091" data-mp="34jw2ni5tfd2p">
              <h4 className="text-sm font-bold text-gray-900 mb-4 flex items-center gap-2" data-id="element-6092" data-mp="3iidlb1gv5zdr">
                <Database className="w-4 h-4 text-gray-400" data-id="element-6093" data-mp="3dhg34ovut5c9" />
                Vraag Metadata
              </h4>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm" data-id="element-6094" data-mp="3segw0vps76cb">
                <div data-id="element-6095" data-mp="3nhgqclwug5lq">
                  <span className="block text-xs text-gray-500 mb-1" data-id="element-6096" data-mp="3ng5oirmyl7x9">
                    Skill
                  </span>
                  <span className="font-medium text-gray-800" data-id="element-6097" data-mp="54sc3qe2cxrg">
                    {question.skill || '—'}
                  </span>
                </div>
                <div data-id="element-6098" data-mp="f02ieqbhvrb1">
                  <span className="block text-xs text-gray-500 mb-1" data-id="element-6099" data-mp="f1diog5qegxe">
                    SubSkill
                  </span>
                  <span className="font-medium text-gray-800" data-id="element-6100" data-mp="9zo811mf859u">
                    {question.subSkill || '—'}
                  </span>
                </div>
                <div data-id="element-6101" data-mp="37wdr523izo96">
                  <span className="block text-xs text-gray-500 mb-1" data-id="element-6102" data-mp="37vvit73l9jkw">
                    Level Code
                  </span>
                  <span className="font-medium text-gray-800" data-id="element-6103" data-mp="3cslyvufe8f6b">
                    {question.levelCode || '—'}
                  </span>
                </div>
                <div data-id="element-6104" data-mp="3mjgmp38afmtv">
                  <span className="block text-xs text-gray-500 mb-1" data-id="element-6105" data-mp="3mjyv0y885ri5">
                    Groep / Tier
                  </span>
                  <div className="flex items-center gap-2" data-id="element-6106" data-mp="3vkwxvd6xfsz5">
                    <span className="font-medium text-gray-800" data-id="element-6107" data-mp="3vlg59zyyayyx">
                      Groep {question.group || '?'}
                    </span>
                    <Badge variant={getTierColor(question.tier)} size="sm" data-id="element-6108" data-mp="2175fnpaj80s3">
                      Tier {question.tier || '?'}
                    </Badge>
                  </div>
                </div>
              </div>
            </div>

            {/* Pedagogy Section */}
            {(question.pedagogy?.dok || question.pedagogy?.misconceptionTags?.length) && <div className="p-4 sm:p-5 border-b border-gray-100 bg-slate-50/50" data-id="element-6109" data-mp="1ttxvn69z64qd">
                <h4 className="text-sm font-bold text-gray-900 mb-3 flex items-center gap-2" data-id="element-6110" data-mp="1owxo0qxh8tx5">
                  <BookOpen className="w-4 h-4 text-indigo-400" data-id="element-6111" data-mp="1jzebpwkcar7u" />
                  Pedagogisch Contract
                </h4>
                <div className="flex flex-wrap gap-4" data-id="element-6112" data-mp="2hiz5mxyghnxr">
                  {question.pedagogy?.dok && <div data-id="element-6113" data-mp="27n5kwral2o4w">
                      <span className="text-xs text-gray-500 block mb-1.5" data-id="element-6114" data-mp="27me3qlhqgsy5">
                        DOK Niveau
                      </span>
                      <Badge variant="neutral" className="bg-indigo-50 text-indigo-700 border-indigo-200" data-id="element-6115" data-mp="23laoo17if0xz">
                        DOK {question.pedagogy.dok}
                      </Badge>
                    </div>}

                  {question.pedagogy?.misconceptionTags && question.pedagogy.misconceptionTags.length > 0 && <div data-id="element-6116" data-mp="2iaqmspvynuvm">
                        <span className="text-xs text-gray-500 block mb-1.5 flex items-center gap-1" data-id="element-6117" data-mp="2ibih7xdtnmkb">
                          <Tag className="w-3 h-3" data-id="element-6118" data-mp="2nalf8xvdwsis" /> Misconcepties
                        </span>
                        <div className="flex flex-wrap gap-1.5" data-id="element-6119" data-mp="3b07bpdkdgtn3">
                          {question.pedagogy.misconceptionTags.map(tag => <Badge key={tag.id} variant="warning" size="sm" data-id="element-6120" data-mp="3f2kdxrzwu9f4">
                              {tag.label || tag.id}
                            </Badge>)}
                        </div>
                      </div>}
                </div>
              </div>}

            {/* Visual Aids Section */}
            {visualCount > 0 && <div className="p-4 sm:p-5 border-b border-gray-100" data-id="element-6121" data-mp="2l5rkbrgpjxd">
                <h4 className="text-sm font-bold text-gray-900 mb-4 flex items-center gap-2" data-id="element-6122" data-mp="bwlb8tyv9yug">
                  <Image className="w-4 h-4 text-purple-400" data-id="element-6123" data-mp="7vrkztxyc7ye" />
                  Visuals ({visualCount})
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-6124" data-mp="lq249vr6j9x2">
                  {question.stimulusAids && question.stimulusAids.length > 0 && <div className="space-y-3" data-id="element-6125" data-mp="3p56i7xo0dari">
                        <h5 className="text-xs font-semibold text-gray-500 uppercase tracking-wider" data-id="element-6126" data-mp="3l3cm82orcuxo">
                          Stimulus Aids
                        </h5>
                        {question.stimulusAids.map((aid, idx) => <div key={`stim-${idx}`} className="bg-gray-50 rounded-lg border border-gray-200 overflow-hidden" data-id="element-6127" data-mp="3u2zloixxfqo3" data-mpi={idx}>
                            <div className="px-3 py-2 bg-gray-100 border-b border-gray-200 flex justify-between items-center" data-id="element-6128" data-mp="19d0moyyv0rxm">
                              <code className="text-xs font-mono font-bold text-purple-700" data-id="element-6129" data-mp="1h8kn2spjhep5">
                                {aid.type}
                              </code>
                              <span className="text-[10px] text-gray-500 bg-white px-1.5 py-0.5 rounded border border-gray-200" data-id="element-6130" data-mp="17f3u1qx883mj">
                                {aid.showWhen || 'always'}
                              </span>
                            </div>
                            <div className="p-4 flex justify-center items-center min-h-[120px] bg-white" data-id="element-6131" data-mp="tgw1rwb4xqhp">
                              <div className="transform scale-75 origin-center w-full max-w-sm" data-id="element-6132" data-mp="oh9f6w9f2xw8">
                                <VisualAidFactory visualAid={aid} mode="stimulus" data-id="element-6133" data-mp="jjretu9gtqzu" />
                              </div>
                            </div>
                          </div>)}
                      </div>}

                  {question.supportAids && question.supportAids.length > 0 && <div className="space-y-3" data-id="element-6134" data-mp="2qds1kr9oh08a">
                      <h5 className="text-xs font-semibold text-gray-500 uppercase tracking-wider" data-id="element-6135" data-mp="2v5t9k0ztc4si">
                        Support Aids
                      </h5>
                      {question.supportAids.map((aid, idx) => <div key={`supp-${idx}`} className="bg-gray-50 rounded-lg border border-gray-200 overflow-hidden" data-id="element-6136" data-mp="23ggkrd5nhfhk" data-mpi={idx}>
                          <div className="px-3 py-2 bg-gray-100 border-b border-gray-200 flex justify-between items-center" data-id="element-6137" data-mp="2cf1ymv06g6c0">
                            <code className="text-xs font-mono font-bold text-amber-700" data-id="element-6138" data-mp="2hcttnxw4c8kf">
                              {aid.type}
                            </code>
                            <span className="text-[10px] text-gray-500 bg-white px-1.5 py-0.5 rounded border border-gray-200" data-id="element-6139" data-mp="3sss7ef28rirz">
                              {aid.showWhen || 'always'}
                            </span>
                          </div>
                          <div className="p-4 flex justify-center items-center min-h-[120px] bg-white" data-id="element-6140" data-mp="3d43utr7z026h">
                            <div className="transform scale-75 origin-center w-full max-w-sm" data-id="element-6141" data-mp="385ahbysoob6b">
                              <VisualAidFactory visualAid={aid} mode="support" data-id="element-6142" data-mp="2kfg7lq3nv2qa" />
                            </div>
                          </div>
                        </div>)}
                    </div>}
                </div>
              </div>}

            {/* Feedback Completeness */}
            <div className="p-4 sm:p-5 bg-gray-50/50" data-id="element-6143" data-mp="21nx3th1tbkn7">
              <h5 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3 flex items-center gap-1.5" data-id="element-6144" data-mp="21mm1llo0mky7">
                <MessageSquare className="w-3.5 h-3.5" data-id="element-6145" data-mp="c7bcdi5o7s5e" />
                Feedback Compleetheid
              </h5>
              <div className="flex flex-wrap gap-2" data-id="element-6146" data-mp="gytalygb7emo">
                <Badge variant={question.feedback?.explanationText ? 'success' : 'danger'} size="sm" data-id="element-6147" data-mp="h0cudz16c7t7">
                  {question.feedback?.explanationText ? <CheckCircle2 className="w-3 h-3 mr-1 inline" data-id="element-6148" data-mp="uzcfiwpha4km" /> : <XCircle className="w-3 h-3 mr-1 inline" data-id="element-6149" data-mp="zwcopghmjnfy" />}
                  Uitleg tekst
                </Badge>
                <Badge variant={question.feedback?.solutionSteps?.length ? 'success' : 'warning'} size="sm" data-id="element-6150" data-mp="lxcxc12q8ug3">
                  {question.feedback?.solutionSteps?.length ? <CheckCircle2 className="w-3 h-3 mr-1 inline" data-id="element-6151" data-mp="3an46212xc5di" /> : <XCircle className="w-3 h-3 mr-1 inline" data-id="element-6152" data-mp="3qyogho058jy2" />}
                  Stappenplan
                </Badge>
                <Badge variant={question.feedback?.distractorExplanations && Object.keys(question.feedback.distractorExplanations).length > 0 ? 'success' : 'warning'} size="sm" data-id="element-6153" data-mp="3h68wym977nb2">
                  {question.feedback?.distractorExplanations && Object.keys(question.feedback.distractorExplanations).length > 0 ? <CheckCircle2 className="w-3 h-3 mr-1 inline" data-id="element-6154" data-mp="3vuwqkmzhhmyd" /> : <XCircle className="w-3 h-3 mr-1 inline" data-id="element-6155" data-mp="2oj5ytv8fpelc" />}
                  Fout-specifieke feedback
                </Badge>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>;
}